import React from 'react';
import { Tooltip, IconButton } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import PropTypes from 'prop-types';
import './history-tooltip.css';

function HistoryTooltip({ history = [], show = false }) {
  const formatValue = (val) => {
    if (val === null || val === undefined || val?.trim() === '') return '--';
    if (typeof val === 'object') return JSON.stringify(val);
    return val;
  };
  // Render history content for tooltip
  const renderHistoryContent = () => {
    if (!show || !Array.isArray(history) || history.length === 0) return null;
    return (
      <div style={{ minWidth: 250, maxWidth: 400 }}>
        <div className="font-bold mb-2 text-gray-800 border-b border-gray-300 pb-2 flex items-center justify-between">
          <span>Change History</span>
          <span className="text-xs font-normal text-gray-500">
            ({history.length} {history.length === 1 ? 'change' : 'changes'})
          </span>
        </div>
        <div
          style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}
          className="pr-1"
        >
          <ul className="space-y-3">
            {history.map((item, idx) => (
              <li key={idx} className="pb-3 border-b border-gray-200 last:border-b-0">
                <div className="flex flex-col gap-1">
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-gray-500 whitespace-nowrap mt-0.5">
                      #{history.length - idx}
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800 mb-1 break-words">
                        {formatValue(item.value)}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {item.recordedAt}
                      </div>
                      {item.changedFields && (
                        <div className="mt-1 text-xs text-purple-700 bg-purple-50 px-2 py-1 rounded break-words">
                          <div className="flex items-start gap-1">
                            <svg
                              className="w-3 h-3 mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <div>
                              <span className="font-medium">Changed fields:</span>{' '}
                              {item.changedFields.split(',').map((field, i, arr) => (
                                <span key={i}>
                                  <span className="font-semibold">{field.trim()}</span>
                                  {i < arr.length - 1 && ', '}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {item.changeReason && (
                        <div className="mt-1 text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded break-words">
                          <span className="font-medium">Reason:</span> {item.changeReason}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  if (!show || !history || history.length === 0) {
    return null;
  }

  return (
    <Tooltip
      title={renderHistoryContent()}
      placement="bottom-end"
      arrow
      classes={{ tooltip: 'custom-history-tooltip', arrow: 'custom-history-arrow' }}
    >
      <IconButton size="small" tabIndex={-1}>
        <HistoryIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

HistoryTooltip.propTypes = {
  show: PropTypes.bool,
  history: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      recordedAt: PropTypes.string,
      changeReason: PropTypes.string,
      changedFields: PropTypes.string,
    })
  ),
};

export default HistoryTooltip;
